/**
 * Sous-ensemble markdown supporté par l'éditeur Break News.
 *
 * Volontairement minimal : gras, italique, barré, listes, titres.
 * Pas de tableaux, pas d'images, pas de HTML brut — donc aucune surface
 * d'injection, et un rendu identique dans le fil, la page du post,
 * les e-mails et les notifications.
 *
 * Ce module est PUR (pas de JSX) : le rendu React vit dans components/RichText.jsx.
 * Le pendant Python est backend/app/media.py::strip_markdown.
 */

// NOTE iOS : aucun lookbehind (?<!...) ici — non supporté par WKWebView avant
// Safari 16.4, et une regex invalide fait planter le bundle entier au parsing.
// Le gras est retiré AVANT l'italique, donc les `*` restants sont sans ambiguïté.
const MD_STRIPPERS = [
  [/\*\*(.+?)\*\*/gs, "$1"], // **gras** (en premier)
  [/\*([^*\n]+)\*/g, "$1"], // *italique*
  [/~~(.+?)~~/gs, "$1"], // ~~barré~~
  [/^\s{0,3}#{1,6}\s+/gm, ""], // # titre
  [/^\s{0,3}[-*]\s+/gm, "• "], // - liste
  [/^\s{0,3}\d+\.\s+/gm, ""], // 1. liste
];

/**
 * Retire les marqueurs markdown.
 *
 * `keepBreaks = false` (défaut) : tout est aplati sur une ligne — extraits,
 * aperçus, méta descriptions.
 *
 * `keepBreaks = true` : les paragraphes sont CONSERVÉS. Utilisé pour le
 * compteur de caractères LinkedIn, qui doit compter exactement ce que le
 * backend composera (voir media.py::strip_markdown, même comportement).
 *
 * NOTE iOS : `[^\S\n]` remplace `[ \t]` étendu — pas de lookbehind ici non plus.
 */
export function stripMarkdown(text, keepBreaks = false) {
  if (!text || typeof text !== "string") return "";
  const stripped = MD_STRIPPERS.reduce(
    (acc, [rx, repl]) => acc.replace(rx, repl),
    text
  );
  if (!keepBreaks) return stripped.replace(/\s+/g, " ").trim();
  return stripped
    .replace(/\r\n?/g, "\n")
    .replace(/[^\S\n]+/g, " ")
    .replace(/[^\S\n]*\n[^\S\n]*/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Extrait borné, coupé sur un mot entier. */
export function excerpt(text, maxLen = 200) {
  const clean = stripMarkdown(text);
  if (clean.length <= maxLen) return clean;
  return `${clean.slice(0, maxLen).replace(/\s+\S*$/, "").replace(/[.,;:!?—-]+$/, "")}…`;
}

/**
 * Insère ou retire un marqueur autour de la sélection d'un <textarea>.
 * Renvoie la nouvelle valeur ET la nouvelle sélection, pour que l'appelant
 * repositionne le curseur (sinon il saute en fin de champ à chaque clic).
 */
export function toggleWrap(value, selStart, selEnd, marker) {
  const before = value.slice(0, selStart);
  const selected = value.slice(selStart, selEnd);
  const after = value.slice(selEnd);
  const len = marker.length;

  // Déjà entouré ? on retire.
  if (
    before.endsWith(marker) &&
    after.startsWith(marker) &&
    selected.length > 0
  ) {
    return {
      value: before.slice(0, -len) + selected + after.slice(len),
      start: selStart - len,
      end: selEnd - len,
    };
  }

  const placeholder = selected || "texte";
  return {
    value: `${before}${marker}${placeholder}${marker}${after}`,
    start: selStart + len,
    end: selStart + len + placeholder.length,
  };
}

/** Préfixe chaque ligne sélectionnée (listes, titres). */
export function togglePrefix(value, selStart, selEnd, prefix) {
  const lineStart = value.lastIndexOf("\n", selStart - 1) + 1;
  const lineEndIdx = value.indexOf("\n", selEnd);
  const lineEnd = lineEndIdx === -1 ? value.length : lineEndIdx;

  const block = value.slice(lineStart, lineEnd);
  const lines = block.split("\n");
  const allPrefixed = lines.every((l) => l.startsWith(prefix));

  const next = lines
    .map((l) => (allPrefixed ? l.slice(prefix.length) : `${prefix}${l}`))
    .join("\n");

  return {
    value: value.slice(0, lineStart) + next + value.slice(lineEnd),
    start: lineStart,
    end: lineStart + next.length,
  };
}

/** Insère du texte à la position du curseur (emoji). */
export function insertAt(value, selStart, selEnd, text) {
  return {
    value: value.slice(0, selStart) + text + value.slice(selEnd),
    start: selStart + text.length,
    end: selStart + text.length,
  };
}
